import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IonCol, IonList, IonItem, IonLabel, IonRow, IonItemDivider, IonToggle } from '@ionic/react'
import { IRootState } from '../../../store'
import { useTranslation } from 'react-i18next'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import { getAllPNNVOptionsSelector, getVitalOptionsSelector } from '../../options/selectors'
import { IOption, IPNNVOption } from '../../../common/common.types'
import {
  saveVitals,
  setApgarAppearance,
  setApgarPulse,
  setApgarGrimace,
  setApgarActivity,
  setApgarRespiration,
  setManualApgarTotalChecked,
  setTotalApgarScore,
  setTotalApgarScorePNNV,
  resetApgarFields,
} from '../store/vitals.actions'
import Validation from '../../../components/validation/Validation.component'
import { VitalsConstants } from '../store/vitals.constants'

const ApgarSection: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const vitals = useSelector((state: IRootState) => state.vitals)
  const {
    apgarAppearance,
    apgarPulse,
    apgarGrimace,
    apgarActivity,
    apgarRespiration,
    totalApgarScore,
    totalApgarScorePNNV,
    __manualApgarTotalChecked,
    key,
  } = vitals

  const vitalOptions = useSelector(getVitalOptionsSelector)
  const {
    apgarAppearanceOptions,
    apgarPulseOptions,
    apgarGrimaceOptions,
    apgarActivityOptions,
    apgarRespirationOptions,
  } = vitalOptions
  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const apgatTotalScorePNNVOptions = allPNNVOptions?.['eVitals.32']

  const apgarAppearanceOnChange = (option: IOption) => {
    dispatch(setApgarAppearance(option))
    dispatch(saveVitals())
  }

  const apgarPulseOnChange = (option: IOption) => {
    dispatch(setApgarPulse(option))
    dispatch(saveVitals())
  }

  const apgarGrimaceOnChange = (option: IOption) => {
    dispatch(setApgarGrimace(option))
    dispatch(saveVitals())
  }

  const apgarActivityOnChange = (option: IOption) => {
    dispatch(setApgarActivity(option))
    dispatch(saveVitals())
  }

  const apgarRespirationOnChange = (option: IOption) => {
    dispatch(setApgarRespiration(option))
    dispatch(saveVitals())
  }

  const manualTotalHandler = () => {
    handleResetApgarFields()
    dispatch(setManualApgarTotalChecked(!__manualApgarTotalChecked))
    dispatch(saveVitals())
  }

  const handleResetApgarFields = () => {
    dispatch(resetApgarFields())
    dispatch(saveVitals())
  }

  const onManualApgarTotalChange = (numbers: string[]) => {
    const value = numbers.join('')
    dispatch(setTotalApgarScore(value))
    dispatch(saveVitals())
  }

  const setApgarTotalPNNV = (option: IPNNVOption | null) => {
    dispatch(setTotalApgarScorePNNV(option))
    dispatch(saveVitals())
  }

  return (
    <>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('vitals.apgar.header')}
        </IonLabel>
      </IonItemDivider>

      <IonList className='ion-no-padding'>
        <IonRow class='ion-align-items-baseline'>
          <IonCol>
            <SingleSelect
              dataTestID='ApgarAppearance'
              options={apgarAppearanceOptions}
              selectedOption={apgarAppearance}
              action={apgarAppearanceOnChange}
              listHeader={t('vitals.apgar.form.Appearance')}
              label={t('vitals.apgar.form.Appearance')}
              showSelectedOptionId={'id'}
              disabled={__manualApgarTotalChecked}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />

        <IonRow class='ion-align-items-baseline'>
          <IonCol>
            <SingleSelect
              dataTestID='ApgarPulse'
              options={apgarPulseOptions}
              selectedOption={apgarPulse}
              action={apgarPulseOnChange}
              listHeader={t('vitals.apgar.form.Pulse')}
              label={t('vitals.apgar.form.Pulse')}
              showSelectedOptionId={'id'}
              disabled={__manualApgarTotalChecked}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />

        <IonRow class='ion-align-items-baseline'>
          <IonCol>
            <SingleSelect
              dataTestID='ApgarGrimace'
              options={apgarGrimaceOptions}
              selectedOption={apgarGrimace}
              action={apgarGrimaceOnChange}
              listHeader={t('vitals.apgar.form.Irritability')}
              label={t('vitals.apgar.form.Irritability')}
              showSelectedOptionId={'id'}
              disabled={__manualApgarTotalChecked}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />

        <IonRow class='ion-align-items-baseline'>
          <IonCol>
            <SingleSelect
              dataTestID='ApgarActivity'
              options={apgarActivityOptions}
              selectedOption={apgarActivity}
              action={apgarActivityOnChange}
              listHeader={t('vitals.apgar.form.Activity')}
              label={t('vitals.apgar.form.Activity')}
              showSelectedOptionId={'id'}
              disabled={__manualApgarTotalChecked}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />

        <IonRow class='ion-align-items-baseline'>
          <IonCol>
            <SingleSelect
              dataTestID='ApgarRespiration'
              options={apgarRespirationOptions}
              selectedOption={apgarRespiration}
              action={apgarRespirationOnChange}
              listHeader={t('vitals.apgar.form.Respiration')}
              label={t('vitals.apgar.form.Respiration')}
              showSelectedOptionId={'id'}
              disabled={__manualApgarTotalChecked}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />

        <IonRow>
          <IonCol size='7'>
            <IonItem lines='none'>
              <IonLabel>{t('vitals.apgar.form.ManualTotal')}</IonLabel>
              <IonToggle
                data-testid={t('vitals.apgar.form.ManualTotal')}
                slot='start'
                name={t('vitals.apgar.form.ManualTotal')}
                checked={__manualApgarTotalChecked}
                onClick={() => manualTotalHandler()}
                color='zoll-blue-secondary'
              />
            </IonItem>
          </IonCol>
          <IonCol size='5'>
            {__manualApgarTotalChecked ? (
              <>
                <IonItem id='ApgarTotal' lines='full'>
                  <NumberPad
                    className='w-100'
                    dataTestId='ManualApgarTotal'
                    label={t('vitals.apgar.form.Total')}
                    values={totalApgarScore ? [totalApgarScore.toString()] : []}
                    onNumPadClick={onManualApgarTotalChange}
                    pnnvAction={setApgarTotalPNNV}
                    pnnvListHeader={t('vitals.apgar.form.Total')}
                    pnnvOptions={apgatTotalScorePNNVOptions}
                    pnnvValue={totalApgarScorePNNV}
                  />
                </IonItem>
                <Validation id={VitalsConstants.SET_TOTAL_APGAR_SCORE} cardinalityKey={key} />
              </>
            ) : (
              <IonItem lines='none'>
                <IonLabel className='ion-text-end apgar-total-label'>
                  {t('vitals.apgar.form.Total')} {totalApgarScore ? totalApgarScore : 'N/A'}
                </IonLabel>
              </IonItem>
            )}
          </IonCol>
        </IonRow>
      </IonList>
    </>
  )
}

export default ApgarSection
